import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { MarketCalendar, estimateCrossCost, type MarginType } from "@yct/core";
import { api, type Broker, type CrossPlan, type UserSettings } from "../lib/api.js";
import { AuthGate } from "../components/AuthGate.js";
import { maybeShowInterstitial } from "../lib/ads.js";
import { ErrorText, Field, marginTypeLabel, statusLabel, yen } from "../components/ui.js";

const cal = new MarketCalendar();

export function CrossFormPage() {
  return (
    <AuthGate>
      <CrossFormInner />
    </AuthGate>
  );
}

function CrossFormInner() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [existing, setExisting] = useState<CrossPlan | null>(null);
  const [error, setError] = useState<unknown>(null);

  const [code, setCode] = useState(params.get("code") ?? "");
  const [name, setName] = useState(params.get("name") ?? "");
  const [lastTradingDay, setLastTradingDay] = useState(params.get("lastTradingDay") ?? "");
  const [brokerId, setBrokerId] = useState("");
  const [marginType, setMarginType] = useState<MarginType>("seido");
  const [shares, setShares] = useState("100");
  const [openDate, setOpenDate] = useState("");
  const [closeDate, setCloseDate] = useState("");
  const [price, setPrice] = useState(params.get("price") ?? "");
  const [gyakuhibu, setGyakuhibu] = useState("");
  const [dividend, setDividend] = useState("");
  const [note, setNote] = useState("");

  // actuals
  const [aGyakuhibu, setAGyakuhibu] = useState("");
  const [aFee, setAFee] = useState("");
  const [aLoan, setALoan] = useState("");
  const [aDiv, setADiv] = useState("");
  const [aClose, setAClose] = useState("");
  const [notExecuted, setNotExecuted] = useState(false);

  useEffect(() => {
    api.brokers().then((r) => {
      setBrokers(r.brokers);
      if (r.brokers[0] && !brokerId) setBrokerId(r.brokers[0].id);
    }).catch(setError);
    api.settings().then(setSettings).catch(setError);
    if (!isNew && id) {
      api.crossPlans().then((r) => {
        const p = r.plans.find((x) => x.id === id);
        if (!p) return;
        setExisting(p);
        setCode(p.stockCode);
        setName(p.stockName ?? "");
        setLastTradingDay(p.lastTradingDay);
        setBrokerId(p.brokerSettingId ?? "");
        setMarginType(p.marginType);
        setShares(String(p.shares));
        setOpenDate(p.openDate);
        setCloseDate(p.closeDate ?? "");
        setPrice(String(p.priceAtPlan));
        setGyakuhibu(p.estGyakuhibuPerShare != null ? String(p.estGyakuhibuPerShare) : "");
        setDividend(p.dividendPerShare != null ? String(p.dividendPerShare) : "");
        setNote(p.note ?? "");
        setAGyakuhibu(p.actualGyakuhibu != null ? String(p.actualGyakuhibu) : "");
        setAFee(p.actualFee != null ? String(p.actualFee) : "");
        setALoan(p.actualLoanCost != null ? String(p.actualLoanCost) : "");
        setADiv(p.actualDividendAdj != null ? String(p.actualDividendAdj) : "");
        setAClose(p.actualCloseDate ?? "");
        setNotExecuted(p.notExecuted);
      }).catch(setError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Default dates follow the 権利付最終日.
  useEffect(() => {
    if (!lastTradingDay || !/^\d{4}-\d{2}-\d{2}$/.test(lastTradingDay)) return;
    if (!openDate) setOpenDate(lastTradingDay);
    if (!closeDate) setCloseDate(cal.addBusinessDays(lastTradingDay, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTradingDay]);

  const broker = brokers.find((b) => b.id === brokerId);
  const estimate = useMemo(() => {
    if (!broker || !price || !shares || !openDate || !closeDate) return null;
    try {
      return estimateCrossCost(cal, {
        price: Number(price),
        shares: Number(shares),
        marginType,
        openDate,
        closeDate,
        ...(gyakuhibu ? { gyakuhibuPerShareTotal: Number(gyakuhibu) } : {}),
        ...(dividend ? { dividendPerShare: Number(dividend) } : {}),
        dividendTaxRate: settings?.dividendTaxRate ?? 0.20315,
        broker,
      });
    } catch {
      return null;
    }
  }, [broker, price, shares, marginType, openDate, closeDate, gyakuhibu, dividend, settings]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const body = {
      stockCode: code.toUpperCase(),
      stockName: name || undefined,
      lastTradingDay,
      brokerSettingId: brokerId,
      marginType,
      shares: Number(shares),
      openDate: openDate || undefined,
      closeDate: closeDate || undefined,
      priceAtPlan: Number(price),
      estGyakuhibuPerShare: gyakuhibu ? Number(gyakuhibu) : null,
      dividendPerShare: dividend ? Number(dividend) : null,
      note: note || undefined,
    };
    try {
      if (isNew) {
        await api.createCrossPlan(body);
      } else if (id) {
        await api.updateCrossPlan(id, {
          ...body,
          actualGyakuhibu: aGyakuhibu ? Number(aGyakuhibu) : null,
          actualFee: aFee ? Number(aFee) : null,
          actualLoanCost: aLoan ? Number(aLoan) : null,
          actualDividendAdj: aDiv ? Number(aDiv) : null,
          actualCloseDate: aClose || null,
          notExecuted,
        });
      }
      navigate("/cross");
      void maybeShowInterstitial();
    } catch (err) {
      setError(err);
    }
  };

  const remove = async () => {
    if (!id || !confirm("この予定を削除しますか？")) return;
    await api.deleteCrossPlan(id);
    navigate("/cross");
  };

  const showActuals = existing && existing.status !== "planned";

  return (
    <section>
      <h1>{isNew ? "クロス予定を追加" : `クロス ${existing ? statusLabel[existing.status] : ""}`}</h1>
      {brokers.length === 0 && <p className="warn">証券会社設定がありません。先に「マイ → 証券会社設定」で追加してください。</p>}
      <form className="form" onSubmit={submit}>
        <Field label="権利付最終日">
          <input className="input" type="date" required value={lastTradingDay} onChange={(e) => setLastTradingDay(e.target.value)} />
        </Field>
        <Field label="証券コード">
          <input className="input" required pattern="[0-9][0-9A-Za-z]{3}" value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        {isNew && lastTradingDay && (
          <p className="muted">
            <Link to={`/search?lastTradingDay=${lastTradingDay}`}>この日が権利付最終日の銘柄を検索</Link>
          </p>
        )}
        <Field label="会社名（任意）">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="証券会社">
          <select className="input" required value={brokerId} onChange={(e) => setBrokerId(e.target.value)}>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="信用区分">
          <select className="input" value={marginType} onChange={(e) => setMarginType(e.target.value as MarginType)}>
            {(["seido", "ippan_short", "ippan_long"] as const).map((m) => (
              <option key={m} value={m}>
                {marginTypeLabel[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="株数">
          <input className="input" type="number" min={1} required value={shares} onChange={(e) => setShares(e.target.value)} />
        </Field>
        <Field label="株価（必要資金の計算用）">
          <input className="input" type="number" min={0} step="0.1" required value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <Field label="建て日">
          <input className="input" type="date" value={openDate} onChange={(e) => setOpenDate(e.target.value)} />
        </Field>
        <Field label="現渡日">
          <input className="input" type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
        </Field>
        {marginType === "seido" && (
          <>
            <Field label="予想逆日歩（1株あたり合計・円）">
              <input className="input" type="number" min={0} step="0.01" value={gyakuhibu} onChange={(e) => setGyakuhibu(e.target.value)} />
            </Field>
            <Field label="予想配当（1株あたり・円）">
              <input className="input" type="number" min={0} step="0.01" value={dividend} onChange={(e) => setDividend(e.target.value)} />
            </Field>
          </>
        )}
        <Field label="メモ">
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>

        {estimate && (
          <div className="card">
            <h2>予想コスト</h2>
            <dl className="dl">
              <dt>約定代金</dt>
              <dd>{yen(estimate.tradeAmount)}</dd>
              <dt>手数料</dt>
              <dd>{yen(estimate.fee)}</dd>
              <dt>貸株料</dt>
              <dd>
                {yen(estimate.loanCost)}（{estimate.loanDays}日、年率 {(estimate.loanRate * 100).toFixed(2)}%）
              </dd>
              <dt>逆日歩</dt>
              <dd>{yen(estimate.gyakuhibuCost)}</dd>
              <dt>配当プラス</dt>
              <dd>-{yen(estimate.dividendPlus)}</dd>
              <dt>合計</dt>
              <dd>
                <strong>{yen(estimate.total)}</strong>
              </dd>
            </dl>
          </div>
        )}

        {showActuals && (
          <div className="card form">
            <h2>実績</h2>
            <label className="check">
              <input type="checkbox" checked={notExecuted} onChange={(e) => setNotExecuted(e.target.checked)} /> 未実施（建てられなかった）
            </label>
            <Field label="実績 逆日歩（円）">
              <input className="input" type="number" value={aGyakuhibu} onChange={(e) => setAGyakuhibu(e.target.value)} />
            </Field>
            <Field label="実績 手数料（円）">
              <input className="input" type="number" value={aFee} onChange={(e) => setAFee(e.target.value)} />
            </Field>
            <Field label="実績 貸株料（円）">
              <input className="input" type="number" value={aLoan} onChange={(e) => setALoan(e.target.value)} />
            </Field>
            <Field label="実績 配当落調整金の差額（円、プラスなら正）">
              <input className="input" type="number" value={aDiv} onChange={(e) => setADiv(e.target.value)} />
            </Field>
            <Field label="実際の現渡日">
              <input className="input" type="date" value={aClose} onChange={(e) => setAClose(e.target.value)} />
            </Field>
          </div>
        )}

        <ErrorText error={error} />
        <div className="row">
          <button className="btn primary" type="submit" disabled={!brokerId}>
            保存
          </button>
          {!isNew && (
            <button className="btn danger" type="button" onClick={remove}>
              削除
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
